import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoginForm } from './LoginForm';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { ResetPasswordForm } from './ResetPasswordForm';
import { LoginLanguageSwitcher } from './LoginLanguageSwitcher';
import { CheckCircle2, AlertCircle, FileText } from 'lucide-react';

interface LoginPageProps {
	redirectTo: string;
	companyLogo?: string;
	companyName?: string;
}

type ViewMode = 'login' | 'forgot-password' | 'reset-password';

export function LoginPage({ redirectTo, companyLogo, companyName = 'Deratix' }: LoginPageProps) {
	const { t } = useTranslation();
	const [viewMode, setViewMode] = useState<ViewMode>('login');
	const [showResetSuccess, setShowResetSuccess] = useState(false);
	const [showSessionExpired, setShowSessionExpired] = useState(false);

	// Get reset data from window.dddLoginData
	const loginData = window.dddLoginData;
	const isResetPage = loginData?.isResetPage ?? false;
	const resetKey = loginData?.resetKey ?? '';
	const resetLogin = loginData?.resetLogin ?? '';
	const loginUrl = loginData?.loginUrl ?? '';

	// Check if this is a reset password page on mount
	useEffect(() => {
		if (isResetPage && resetKey && resetLogin) {
			setViewMode('reset-password');
		}

		// Check for URL parameters
		const params = new URLSearchParams(window.location.search);

		// Check for reset=success (after successful password reset)
		if (params.get('reset') === 'success') {
			setShowResetSuccess(true);
			// Clean URL
			window.history.replaceState({}, '', window.location.pathname);
		}

		// Check for session_expired=1 (after auth error redirect)
		if (params.get('session_expired') === '1') {
			setShowSessionExpired(true);
			// Clean URL
			window.history.replaceState({}, '', window.location.pathname);
		}
	}, [isResetPage, resetKey, resetLogin]);

	const getTitle = () => {
		switch (viewMode) {
			case 'forgot-password':
				return t('Obnovenie hesla');
			case 'reset-password':
				return t('Nové heslo');
			default:
				return t('Prihlásenie');
		}
	};

	const handleResetSuccess = () => {
		// Redirect to login page with success message
		if (loginUrl) {
			window.location.href = loginUrl + '?reset=success';
		} else {
			// Fallback: just show success and switch to login
			setShowResetSuccess(true);
			setViewMode('login');
		}
	};

	const handleBackToLogin = () => {
		if (isResetPage && loginUrl) {
			// If on reset page, redirect to login page
			window.location.href = loginUrl;
		} else {
			setViewMode('login');
		}
	};

	return (
		<div
			data-ui="login-page-container"
			className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4"
		>
			<Card data-ui="login-page-card" className="w-full max-w-md shadow-lg">
				<CardHeader className="space-y-4 pb-4">
					{/* Logo */}
					<div data-ui="login-page-logo" className="flex justify-center">
						{companyLogo ? (
							<img
								src={companyLogo}
								alt={companyName}
								className="h-16 w-auto object-contain"
							/>
						) : (
							<div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
								<FileText className="h-8 w-8 text-primary" />
							</div>
						)}
					</div>

					{/* Title */}
					<div className="text-center space-y-1">
						<h1
							data-ui="login-page-title"
							className="text-2xl font-semibold tracking-tight"
						>
							{getTitle()}
						</h1>
					</div>
				</CardHeader>

				<CardContent>
					{/* Session expired message */}
					{showSessionExpired && viewMode === 'login' && (
						<Alert
							data-ui="login-page-session-expired-alert"
							className="mb-4 border-amber-200 bg-amber-50 text-amber-800"
						>
							<AlertCircle className="h-4 w-4 text-amber-600" />
							<AlertDescription>
								{t('Vaša relácia vypršala. Prihláste sa znovu.')}
							</AlertDescription>
						</Alert>
					)}

					{/* Reset success message */}
					{showResetSuccess && viewMode === 'login' && (
						<Alert
							data-ui="login-page-reset-success-alert"
							className="mb-4 border-green-200 bg-green-50 text-green-800"
						>
							<CheckCircle2 className="h-4 w-4 text-green-600" />
							<AlertDescription>
								{t('Heslo bolo úspešne zmenené. Teraz sa môžete prihlásiť.')}
							</AlertDescription>
						</Alert>
					)}

					{viewMode === 'login' && (
						<LoginForm
							redirectTo={redirectTo}
							onForgotPassword={() => setViewMode('forgot-password')}
						/>
					)}

					{viewMode === 'forgot-password' && (
						<ForgotPasswordForm onBack={() => setViewMode('login')} />
					)}

					{viewMode === 'reset-password' && (
						<ResetPasswordForm
							resetKey={resetKey}
							resetLogin={resetLogin}
							onSuccess={handleResetSuccess}
							onBack={handleBackToLogin}
						/>
					)}
				</CardContent>
			</Card>

			{/* Language Switcher */}
			<LoginLanguageSwitcher locales={window.dddLoginData?.availableLocales || []} />

			{/* Footer */}
			<div className="fixed bottom-4 text-center text-xs text-muted-foreground">
				&copy; {new Date().getFullYear()} {companyName}
			</div>
		</div>
	);
}
